import * as React from 'react';

import { BaseButton, IButtonProps, memoizeFunction } from '@fluentui/react';
import { Callout, DirectionalHint } from '@fluentui/react/lib/Callout';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Facepile, OverflowButtonType } from '@fluentui/react/lib/Facepile';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import { ITheme } from '@fluentui/react/lib/Styling';
import { getInitials, KeyCodes } from '@fluentui/react/lib/Utilities';

import { getInputMethodFromEvent, InputMethod } from '@ms/acui-common';
import { AppContext } from '@ms/acui-context';
import { checkExperiment, ribbonExperiments } from '@ms/acui-experiments';
import { AppThemeConsumer } from '@ms/acui-theme';
import { AppTooltipHost } from '@ms/acui-tooltip';
import { getBoundingWindowWithPaddingFabric } from '@ms/window-util';

import { CoauthorGalleryID } from '../../CollabConstants';
import { ActionHub } from '../ActionHub';
import { CoauthorInformation, getPersonaProps } from '../CoauthorInformation';
import { onRenderCoauthorSection } from '../Renderers';
import {
  CoauthorGalleryPalette,
  getCoauthorGalleryPalette,
  getCoauthorSectionDividerStyle,
  getDescriptiveOverflowStyles,
  getOverflowMenuStyles,
  getOverflowStartGroupChatStyles,
} from '../Styles';
import { getFacepileCoauthorCount, sortCoauthors } from '../utilities';
import { CoauthorGalleryProps } from './CoauthorGallery.Props';
import { getCalloutStyles, getCoauthorGalleryClassNames, getFacepileStyles } from './CoauthorGallery.styles';

export interface CoauthGalleryState {
  actionHubCoauthor?: CoauthorInformation;
  hoveredCoauthor?: CoauthorInformation;
  actionHubLaunchMethod?: InputMethod;
  coauthorsByJoinTime: CoauthorInformation[];
  overflowOpened: boolean;
}

export class CoauthorGallery extends React.Component<CoauthorGalleryProps, CoauthGalleryState> {
  private readonly actionHubCalloutDelayMs: number;
  private readonly overflowButtonId: string;
  private overflowButton = React.createRef<BaseButton>();
  private tooltipHost: AppTooltipHost | undefined;
  private tooltipHostRegistered: boolean;
  private actionHubTimeout: number;
  private calloutPinned: boolean;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public constructor(props: CoauthorGalleryProps) {
    super(props);
    this.state = {
      actionHubCoauthor: undefined,
      hoveredCoauthor: undefined,
      actionHubLaunchMethod: InputMethod.Unknown,
      coauthorsByJoinTime: sortCoauthors(props.coauthors, true),
      overflowOpened: false,
    };
    this.actionHubCalloutDelayMs = 700;
    this.actionHubTimeout = 0;
    this.calloutPinned = false;
    this.overflowButtonId = 'Coauthor Gallery Overflow';
    this.tooltipHostRegistered = false;
  }

  // We only need to update if the coauthors have changed, the amount of coauthors to show has changed, or we have interacted with a coauthor
  public shouldComponentUpdate(nextProps: CoauthorGalleryProps, nextState: CoauthGalleryState) {
    return (
      nextProps.maxFacepileCoauthors !== this.props.maxFacepileCoauthors ||
      JSON.stringify(nextProps.coauthors) !== JSON.stringify(this.props.coauthors) ||
      nextState.hoveredCoauthor !== this.state.hoveredCoauthor ||
      nextState.actionHubCoauthor !== this.state.actionHubCoauthor
    );
  }

  public render() {
    this.calloutPinned = Boolean(this.state.actionHubCoauthor && this.calloutPinned);
    const {
      coauthors,
      maxFacepileCoauthors,
      smallFallBackUserImage,
      getActionHubCommandsToRender,
      styles,
    } = this.props;

    return (
      <AppThemeConsumer>
        {(theme: ITheme) => {
          const palette = getCoauthorGalleryPalette(theme, this.props.styles?.palette);
          const overflowProps = this.generateOverflowProps(coauthors, palette);

          return (
            <div
              className={getCoauthorGalleryClassNames(this.props.styles).root}
              onMouseEnter={this.props.onMouseEnter}
              onMouseLeave={this.onDelayedCalloutClearNeeded}
              data-unique-id={this.props.id ?? CoauthorGalleryID}
              data-automation-type={this.props.dataAutomationType ?? CoauthorGalleryID}
              id={CoauthorGalleryID}
            >
              <Facepile
                key={'CoauthorGallery'}
                styles={getFacepileStyles(palette, styles?.facepileStyles)}
                personas={this.state.coauthorsByJoinTime.map(this.convertCoauthor)}
                personaSize={PersonaSize.size28}
                getPersonaProps={getPersonaProps(palette.ribbonCoinSize, palette, smallFallBackUserImage)}
                overflowButtonType={OverflowButtonType.descriptive}
                overflowButtonProps={overflowProps}
                maxDisplayablePersonas={getFacepileCoauthorCount(maxFacepileCoauthors, coauthors.length)}
              />
              {this.state.actionHubCoauthor && (
                <Callout
                  /** Id of the coauthor gallery overflow menu items are the coauthor's Id + -MenuItem. Id of the controls in the face piles is just the coauthor's Id.
                  Append -MenuItem to the coauthor's id to create the id of target element when user clicks on a person in the overflow */
                  target={`[id='${
                    this.state.actionHubCoauthor.id +
                    (this.props.isPeopleCardEnabled && this.state.overflowOpened ? '-MenuItem' : '')
                  }']`}
                  directionalHint={DirectionalHint.bottomCenter}
                  styles={getCalloutStyles(palette)}
                  setInitialFocus={this.state.actionHubLaunchMethod === InputMethod.Keyboard}
                  gapSpace={palette.actionHubCalloutGap}
                  onMouseEnter={this.onCalloutClearTimeout}
                  onMouseLeave={this.onCalloutClearNeeded}
                  onDismiss={this.onCalloutDismiss}
                  bounds={getBoundingWindowWithPaddingFabric}
                >
                  <ActionHub
                    palette={palette}
                    coauthorInfo={this.state.actionHubCoauthor}
                    /* Always show persona controls in action hub except when people card feature is enabled and action hub is not opened in the overflow menu*/
                    shouldRenderPersonaControl={this.props.isPeopleCardEnabled ? !this.state.overflowOpened : true}
                    fallBackUserImage={this.props.largeFallBackUserImage}
                    setFocusOnMount={this.state.actionHubLaunchMethod === InputMethod.Keyboard}
                    commandsToRender={
                      getActionHubCommandsToRender
                        ? getActionHubCommandsToRender(
                            this.state.actionHubCoauthor,
                            this.state.coauthorsByJoinTime.indexOf(this.state.actionHubCoauthor),
                            this.state.overflowOpened
                          )
                        : []
                    }
                  />
                </Callout>
              )}
            </div>
          );
        }}
      </AppThemeConsumer>
    );
  }

  private onOverflowOpened = (): void => {
    if (this.tooltipHost) {
      this.tooltipHost.hideAllTooltips(true);
    }
    if (this.props.onAfterMenuOpen) {
      this.props.onAfterMenuOpen({ id: this.overflowButtonId });
    }
    this.setState({ actionHubCoauthor: undefined, overflowOpened: true });
  };

  private onOverflowMenuDismissed = (): void => {
    if (this.tooltipHost) {
      this.tooltipHost.hideAllTooltips(false);
    }
    this.onAfterMenuClose();
    this.setState({ overflowOpened: false });
  };

  private onAfterMenuClose = (): void => {
    if (this.props.onAfterMenuClose) {
      this.props.onAfterMenuClose();
    }
  };

  private onAfterMenuOpen = () => {
    if (this.props.onAfterMenuOpen && this.state.actionHubCoauthor) {
      this.props.onAfterMenuOpen({ id: this.state.actionHubCoauthor.id });
    }
  };

  /**
   * A handler that can reset the state of the coauthor gallery to hide the action hub and reset the calloutPinned boolean. It will reset
   * after a delay to allow for the user to mouse from the coauthor to the action hub
   */
  private onDelayedCalloutClearNeeded = () => {
    if (!this.calloutPinned) {
      this.onCalloutClearTimeout();
      this.actionHubTimeout = window.setTimeout(() => {
        this.setState({
          hoveredCoauthor: undefined,
          actionHubCoauthor: undefined,
          actionHubLaunchMethod: InputMethod.Unknown,
        });
      }, this.actionHubCalloutDelayMs);
    } else {
      this.setState({ hoveredCoauthor: undefined, actionHubLaunchMethod: InputMethod.Unknown });
    }
  };

  private onCalloutClearNeeded = () => {
    if (!this.calloutPinned) {
      this.onCalloutClearTimeout();
      this.setState({
        hoveredCoauthor: undefined,
        actionHubCoauthor: undefined,
        actionHubLaunchMethod: InputMethod.Unknown,
      });
    } else {
      this.setState({ hoveredCoauthor: undefined, actionHubLaunchMethod: InputMethod.Unknown });
    }
  };

  private onCalloutDismiss = (ev?: any) => {
    if (this.calloutPinned) {
      this.onAfterMenuClose();
    }
    this.calloutPinned = false;
    const actionHubCoauthor = this.state.actionHubCoauthor;
    const which = ev && ev.which;

    this.setState(
      { actionHubCoauthor: undefined, hoveredCoauthor: undefined, actionHubLaunchMethod: InputMethod.Unknown },
      () => {
        if (which === KeyCodes.escape) {
          // TODO: Once Fabric allows for button props to be passed to Facepile Buttons get rid of this logic and use refs.
          const target = actionHubCoauthor && document.getElementById(actionHubCoauthor.id);
          target && target.focus();
        }
      }
    );
  };

  /**
   * This handler will take care of all the situations involving creating an action hub and getting it to appear. We have a few cases
   * that we need to handle. They are:
   *      1). This function was called from an onMouseEnter event.
   *        A). If there is an actionhub visible and it is not pinned. Display a new actionHub with the currently hovered over coauthor
   *            as the source.
   *        B). If there is not an actionhub visible. Set the currently hovered coauthor as the hoveredCoauthor in the the state and then
   *            create an actionHub after a short delay.
   *        C). If there is an actionhub visible and it is pinned. Just change the state to register the newly hovered Coauthor.
   *      2). This function was called from a MouseClick event.
   *        A). If the coauthor clicked is currently showing in the action hub. Dismiss the action hub if it is pinned or pin it.
   *        B). If the coauthor clicked is not currently showing in the action hub. Pin the new coauthor and create an actionhub.
   * @param coauthor The coauthor which will have its information consumed by the action hub.
   * @param hoverAction We need to know if a hover action triggered the handler or if it was an execute event to decide
   * if we want to pin the hub or not.
   */
  private setUpActionHub = (
    coauthor: CoauthorInformation,
    hoverAction?: boolean
  ): ((ev: React.MouseEvent<HTMLDivElement>) => void) => {
    return (ev: React.MouseEvent<HTMLDivElement>) => {
      this.onCalloutClearTimeout();
      const inputMethod = getInputMethodFromEvent(ev);
      if (hoverAction) {
        /**
         * In this case a us hovers over a persona when an action hub is currently open and no callout is pinned. This happens whens someone goes
         * from hovering one coauthor to another before the action hub timeout elapses and the first callout is cleared. It also occurs if someone
         * exits hovering a coauthor and hovers over them again before the action hub timeout elapses and the callout is cleared.
         */
        if (this.state.actionHubCoauthor && !this.calloutPinned) {
          this.setState({ actionHubCoauthor: coauthor, hoveredCoauthor: coauthor, actionHubLaunchMethod: inputMethod });
          /**
           * In this case, a hover action is occurring and no action hub is currently open. First, hoveredCoauthor is set as the @param coauthor.
           * Then, the action hub is opened for @param coauthor by setting the state's coauthor field after the action hub delay timeout has elapsed.
           * Before opening the action hub, the overflow menu is dismissed if open.
           */
        } else if (!this.state.actionHubCoauthor) {
          this.setState({ hoveredCoauthor: coauthor, actionHubLaunchMethod: inputMethod });
          this.actionHubTimeout = window.setTimeout(() => {
            this.overflowButton.current && this.overflowButton.current.dismissMenu();
            this.setState({ actionHubCoauthor: coauthor, actionHubLaunchMethod: inputMethod });
          }, this.actionHubCalloutDelayMs);
          /** For any other hover action, set the state's hoveredCoauthor to @param coauthor with no delay. */
        } else {
          this.setState({ hoveredCoauthor: coauthor, actionHubLaunchMethod: inputMethod });
        }
        /** This condition indicates that this was a non-hover action (e.g. click, opened from keytip, etc.) */
      } else {
        /** This checks if the action hub for @param coauthor is currently open */
        if (this.state.actionHubCoauthor === coauthor) {
          /**
           * If the action hub for @param coauthor is open and a callout is pinned, toggle the state of the action hub. This means changing the flag of
           * @var calloutPinned to false and then clearing the callout.
           */
          if (this.calloutPinned) {
            this.calloutPinned = false;
            this.onCalloutClearNeeded();
            /**
             * If a callout is not pinned, but the callout for @param coauthor is already open, the user must be trying to pin the already open callout.
             * Set @var calloutPinned to true.
             */
          } else {
            this.calloutPinned = true;
          }
          /**
           * If there is no callout open or the current callout is not the one corresponding to @param coauthor, the user must
           * be pinning the coauthor before it's action hub has opened. Set @var calloutPinned to true and set the state (with no delay)
           * so that coauthor and hoveredCoauthor are @param coauthor.
           */
        } else {
          this.calloutPinned = true;
          this.setState(
            { actionHubCoauthor: coauthor, hoveredCoauthor: coauthor, actionHubLaunchMethod: inputMethod },
            () => this.onAfterMenuOpen()
          );
        }
      }
    };
  };

  private createActionHubHandler = (coauthor: CoauthorInformation): ((el?: HTMLElement) => void) => {
    return () => {
      this.calloutPinned = true;
      this.setState({ actionHubCoauthor: coauthor, actionHubLaunchMethod: InputMethod.Keyboard }, () =>
        this.onAfterMenuOpen()
      );
    };
  };

  private onCalloutClearTimeout = (): void => {
    clearTimeout(this.actionHubTimeout);
  };

  /**
   * Take the coauthor information props and convert them into an IFacepilePersona. If the coauthor has an image and is
   * being used for the action hub or is currently hovered over, show their initials instead of their image.
   */
  private convertCoauthor = (coauthor: CoauthorInformation, index: number) => {
    const { onConvertCoauthor } = this.props;
    const customProps = onConvertCoauthor && onConvertCoauthor(coauthor, index, this.createActionHubHandler);
    return {
      id: coauthor.id,
      onClick: this.setUpActionHub(coauthor),
      imageUrl:
        this.state.actionHubCoauthor === coauthor || this.state.hoveredCoauthor === coauthor
          ? undefined
          : coauthor.imageUrl,
      onMouseEnter: this.setUpActionHub(coauthor, true),
      onMouseLeave: this.onDelayedCalloutClearNeeded,
      imageInitials: getInitials(coauthor.name, false),
      'aria-label': `${coauthor.name} ${coauthor.description}`,
      'data-automation-type': `RibbonButton`,
      'data-unique-id': coauthor.id,
      ...customProps,
      data: {
        isAnonymous: coauthor.isAnonymous,
        name: coauthor.name,
        color: coauthor.color,
        hasRing: coauthor.hasRing,
        ...(customProps && customProps.data),
      },
    };
  };

  private updateOverflowTooltip = () => {
    const controlLabel = this.props.overflowTooltip ?? this.props.overflowMenuHeaderText;
    if (
      this.context.setTooltipElement &&
      this.context.updateTooltipsHidden &&
      this.props.coauthors.length > this.props.maxFacepileCoauthors &&
      controlLabel
    ) {
      // If there was already for a tooltip for the overflow, we need to unregister all actions.
      if (this.tooltipHost && this.tooltipHostRegistered) {
        this.tooltipHost.unregisterTooltipHostActions();
      }
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        refElement: this.overflowButton,
        controlId: this.overflowButtonId,
        controlLabel,
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: !!checkExperiment(this.context, ribbonExperiments.delayRenderTooltipsIsEnabled),
      });
      this.tooltipHost.registerTooltipHostActions();
      this.tooltipHostRegistered = true;
    }
  };

  componentDidUpdate(prevProps: CoauthorGalleryProps) {
    const { coauthors, maxFacepileCoauthors } = this.props;
    const facepileCoauthorsCount = getFacepileCoauthorCount(maxFacepileCoauthors, coauthors.length);
    // A sudden change in coauthors can cause the actionhub to position incorrectly. Add a check to make sure the coauthor is still in the pile
    const coauthorInFacepile = this.state.coauthorsByJoinTime
      .slice(0, facepileCoauthorsCount)
      .some(coauthor => coauthor.id === (this.state.actionHubCoauthor && this.state.actionHubCoauthor.id));
    if (
      !coauthorInFacepile &&
      this.state.actionHubCoauthor &&
      (this.props.isPeopleCardEnabled ? !this.state.overflowOpened : true)
    ) {
      this.calloutPinned = false;
      this.setState({
        actionHubCoauthor: undefined,
        hoveredCoauthor: undefined,
        actionHubLaunchMethod: InputMethod.Unknown,
      });
    }
    // Do the following to check if we need to update the overflow tooltip
    // Check if we have an overflow button
    if (this.state.coauthorsByJoinTime.length > maxFacepileCoauthors) {
      // If we dont have a tooltip for the overflow button or the amount of coauthors changes. Call update tooltip
      if (!this.tooltipHostRegistered || prevProps.coauthors.length !== this.props.coauthors.length) {
        this.updateOverflowTooltip();
      }
    } // If there is a tooltip and no overflow button. Unregister the tooltip
    else if (this.tooltipHost && this.tooltipHostRegistered) {
      this.tooltipHost.unregisterTooltipHostActions();
    }
  }

  static getDerivedStateFromProps(props: CoauthorGalleryProps) {
    return {
      coauthorsByJoinTime: sortCoauthors(props.coauthors),
    };
  }

  componentDidMount() {
    this.updateOverflowTooltip();
    this.props.onCoauthorsMounted && this.props.onCoauthorsMounted(new Date().getTime());
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
    }
  }

  private onCoauthorMenuItemExecuteHelper = (
    ev: React.MouseEvent<HTMLDivElement>,
    coauthor: CoauthorInformation
  ): void => {
    if (this.props.isPeopleCardEnabled) {
      // coauthors inside the overflow have the -MenuItem infront of their ID.
      this.setUpActionHub(coauthor, false)(ev);
    } else {
      this.props.onCoauthorMenuItemExecute && this.props.onCoauthorMenuItemExecute(coauthor);
    }
  };

  private generateMenuItems = (palette: CoauthorGalleryPalette) => {
    const {
      overflowMenuHeaderText,
      coauthors,
      largeFallBackUserImage,
      getAdditionalOverFlowCommands,
      numberOfPresentUsersDescriptor,
    } = this.props;

    let overflowMenuItems: IContextualMenuItem[] = [
      {
        name: numberOfPresentUsersDescriptor?.replace('{0}', coauthors.length.toString()) ?? overflowMenuHeaderText,
        key: 'coauthor-menu-title',
        itemType: ContextualMenuItemType.Header,
      },
      {
        key: 'coauthor-menu-coauthors',
        itemType: ContextualMenuItemType.Normal,
        onRender: onRenderCoauthorSection(
          coauthors,
          palette,
          largeFallBackUserImage,
          this.onCoauthorMenuItemExecuteHelper
        ),
      },
    ];

    const additionalOverFlowCommand =
      (getAdditionalOverFlowCommands &&
        getAdditionalOverFlowCommands(coauthors, getOverflowStartGroupChatStyles(palette))) ??
      [];

    if (additionalOverFlowCommand.length > 0) {
      overflowMenuItems = overflowMenuItems.concat([
        {
          key: 'divider',
          itemType: ContextualMenuItemType.Divider,
          itemProps: { styles: getCoauthorSectionDividerStyle(palette) },
        },
        ...additionalOverFlowCommand,
      ]);
    }

    return overflowMenuItems;
  };

  private generateOverflowProps = memoizeFunction(
    (_coauthors: CoauthorInformation[], palette: CoauthorGalleryPalette): IButtonProps => {
      const { styles, customOverflowProps } = this.props;

      return {
        menuProps: {
          shouldFocusOnMount: true,
          items: this.generateMenuItems(palette),
          directionalHintFixed: true,
          isBeakVisible: true,
          gapSpace: palette.overflowCalloutGap,
          styles: getOverflowMenuStyles(palette, styles?.overflowMenuStyles),
          directionalHint: DirectionalHint.bottomCenter,
          onMenuOpened: this.onOverflowOpened,
          onMenuDismissed: this.onOverflowMenuDismissed,
        },
        title: undefined,
        ariaLabel: this.props.overflowTooltip ?? this.props.overflowMenuHeaderText,
        onRenderMenuIcon: () => null,
        componentRef: this.overflowButton,
        ...customOverflowProps,
        styles: getDescriptiveOverflowStyles(palette, customOverflowProps?.styles),
      };
    },
    1
  );
}
